import type { PropsWithChildren, JSX } from 'react';
import { createContext, useContext } from 'react';

/**
 * Marks a listing surface whose tiles open a detail page in the mobile
 * app-mode presentation: the site header is hidden and a floating back
 * chevron dismisses back to the listing, like a pushed screen in a native
 * app. Category, browse and search pages wrap their content in
 * {@link TileAppModeScope}; a tile rendered anywhere else — a related row on
 * a detail page, a homepage strip — navigates to an ordinary page with its
 * header intact, so browsing from detail page to detail page never buries
 * the header behind a back stack.
 *
 * This scope does not gate the hero morph. That runs wherever a tile does,
 * behind the `revamp_2026` flag alone
 * (`uiContextAtomViewTransitionsEnabled`).
 */
const TileAppModeScopeContext = createContext<boolean>(false);

/** Whether a tile on this surface opens detail pages in app mode. */
export const useTileAppModeScope = (): boolean =>
    useContext(TileAppModeScopeContext);

/** Wraps a listing surface whose tiles opt into the app-mode presentation. */
export const TileAppModeScope = ({
    children,
}: PropsWithChildren): JSX.Element => (
    <TileAppModeScopeContext.Provider value={true}>
        {children}
    </TileAppModeScopeContext.Provider>
);
